/**
 * Hook for onboarding readiness calculation (DRR-184)
 *
 * Calculates completion status across 5W1H dimensions by analyzing
 * task completion within each task group. Returns per-ring and overall
 * readiness status.
 *
 * CONSTRAINTS:
 * - Dumb Frontend: All task data comes from backend query
 * - Task groups defined by backend, not hardcoded here
 * - No domain-specific assumptions (works for any constellation)
 */

import { useMemo } from 'react';
import { GqlTaskStatus, useGetOnboardingTasksQuery } from '@/generated/graphql';

// 5W1H dimension definitions mapped to task groups (DRR-184)
// These are the universal onboarding dimensions - any domain maps to these
export const ONBOARDING_DIMENSIONS = [
  {
    id: 'WHO',
    label: 'Who',
    description: 'Staff roles, certifications, background studies, team assignments',
    color: 'blue',
    taskGroupRefs: ['tg-0-1-001'],
  },
  {
    id: 'WHAT',
    label: 'What',
    description: 'Workflows activated, form schemas provisioned, task group bindings',
    color: 'teal',
    taskGroupRefs: ['tg-0-1-002'],
  },
  {
    id: 'WHY',
    label: 'Why',
    description: 'Rulesets bound, regulatory mandates active, compliance edges verified',
    color: 'red',
    taskGroupRefs: ['tg-0-1-003'],
  },
  {
    id: 'HOW',
    label: 'How',
    description: 'Form schema overrides applied, portal automation configured, SOPs imported',
    color: 'violet',
    taskGroupRefs: ['tg-0-1-004'],
  },
  {
    id: 'WHERE',
    label: 'Where',
    description: 'Floor plan uploaded, room/zone mapping, spatial coordinates pinned',
    color: 'orange',
    taskGroupRefs: ['tg-0-1-005'],
  },
  {
    id: 'WHEN',
    label: 'When',
    description: 'Shift schedules, license renewals, regulatory deadlines, training due dates',
    color: 'cyan',
    taskGroupRefs: ['tg-0-1-006'],
  },
] as const;

export type ReadinessLevel = 'RED' | 'YELLOW' | 'GREEN';

export interface DimensionStats {
  id: string;
  label: string;
  description: string;
  color: string;
  taskGroupRefs: readonly string[];
  total: number;
  completed: number;
  blocked: number;
  readiness: ReadinessLevel;
  percentComplete: number;
}

export interface OnboardingReadinessData {
  dimensions: DimensionStats[];
  overallCompleted: number;
  overallTotal: number;
  overallPercent: number;
  overallStatus: ReadinessLevel;
  blockingRings: string[];
  isReady: boolean;
  loading: boolean;
  error: Error | undefined;
}

function getReadiness(total: number, completed: number): ReadinessLevel {
  if (total === 0) return 'RED';
  const pct = completed / total;
  if (pct >= 1) return 'GREEN';
  if (pct >= 0.5) return 'YELLOW';
  return 'RED';
}

export const READINESS_COLORS: Record<ReadinessLevel, string> = {
  RED: 'red',
  YELLOW: 'yellow',
  GREEN: 'green',
};

export const READINESS_LABELS: Record<ReadinessLevel, string> = {
  RED: 'Not Ready',
  YELLOW: 'In Progress',
  GREEN: 'Ready',
};

/**
 * Calculate onboarding readiness across 5W1H dimensions
 *
 * @example
 * const { dimensions, overallStatus, blockingRings, isReady } = useOnboardingReadiness();
 */
export function useOnboardingReadiness(): OnboardingReadinessData {
  const { data, loading, error } = useGetOnboardingTasksQuery();

  const dimensionStats = useMemo(() => {
    const tasks = data?.tasks ?? [];
    return ONBOARDING_DIMENSIONS.map((dim) => {
      const refs = dim.taskGroupRefs as readonly string[];
      const dimTasks = tasks.filter(
        (t) => t.taskGroupRef && refs.includes(t.taskGroupRef),
      );
      const total = dimTasks.length;
      const completed = dimTasks.filter((t) => t.status === GqlTaskStatus.COMPLETED).length;
      const blocked = dimTasks.filter((t) => t.status === GqlTaskStatus.BLOCKED).length;
      const readiness = getReadiness(total, completed);
      const percentComplete = total > 0 ? Math.round((completed / total) * 100) : 0;
      return {
        ...dim,
        total,
        completed,
        blocked,
        readiness,
        percentComplete,
      };
    });
  }, [data]);

  const overallCompleted = dimensionStats.reduce((sum, d) => sum + d.completed, 0);
  const overallTotal = dimensionStats.reduce((sum, d) => sum + d.total, 0);
  const overallPercent = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0;

  // Ring 3 (WHY) is critical - if incomplete, status is RED
  const ring3 = dimensionStats.find((d) => d.id === 'WHY');
  const ring3Incomplete = ring3?.readiness !== 'GREEN';

  // Any other ring incomplete -> YELLOW
  const anyOtherIncomplete = dimensionStats.some(
    (d) => d.id !== 'WHY' && d.readiness !== 'GREEN',
  );

  // Determine overall status
  let overallStatus: ReadinessLevel;
  if (ring3Incomplete) {
    overallStatus = 'RED';
  } else if (anyOtherIncomplete) {
    overallStatus = 'YELLOW';
  } else {
    overallStatus = 'GREEN';
  }

  // Find blocking rings
  const blockingRings = dimensionStats
    .filter((d) => d.readiness !== 'GREEN')
    .map((d) => d.id);

  return {
    dimensions: dimensionStats,
    overallCompleted,
    overallTotal,
    overallPercent,
    overallStatus,
    blockingRings,
    isReady: overallStatus === 'GREEN',
    loading,
    error: error as Error | undefined,
  };
}

export default useOnboardingReadiness;
